import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { ArrowLeft, ExternalLink, Code2, Sparkles, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { YachtLogo } from '../components/YachtLogo';

import Navbar from '../components/Navbar';

export default function Gallery() {
  const [deployments, setDeployments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDeployments = async () => {
      try {
        const q = query(
          collection(db, 'deployments'),
          orderBy('deployedAt', 'desc'),
          limit(50)
        );
        const snapshot = await getDocs(q);
        let deps: any[] = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        if (deps.length === 0) {
          deps = [
            { id: 'saas-starter', title: 'SaaS Alpha Engine', description: 'A complete multi-tenant SaaS architecture synthesized in 14 minutes.', status: 'Production', deployedAt: { seconds: Date.now() / 1000 - 86400 } },
            { id: 'ecomm-stack', title: 'headless_commerce', description: 'Next-gen commerce synthesis with automated inventory kinetics.', status: 'Live', deployedAt: { seconds: Date.now() / 1000 - 172800 } },
            { id: 'ai-dashboard', title: 'Neural Monitor V2', description: 'Real-time inference tracking with acoustics-ready visualizers.', status: 'Online', deployedAt: { seconds: Date.now() / 1000 - 259200 } },
            { id: 'social-graph', title: 'Nexus Protocol', description: 'Autonomous social graph with identity synthesis layers.', status: 'Active', deployedAt: { seconds: Date.now() / 1000 - 345600 } },
            { id: 'fin-bridge', title: 'Liquid Ledger', description: 'High-frequency transaction synthesis and audit logic.', status: 'Live', deployedAt: { seconds: Date.now() / 1000 - 432000 } },
            { id: 'asset-forge', title: 'Genesis Builder', description: 'The foundation for multi-modal asset synthesis and deployment.', status: 'Ready', deployedAt: { seconds: Date.now() / 1000 - 518400 } },
          ];
        }

        setDeployments(deps);
      } catch (err: any) {
        console.error('Error fetching gallery deployments:', err);
        // Fallback to featured on error
        setDeployments([
          { id: 'saas-starter', title: 'SaaS Alpha Engine', description: 'A complete multi-tenant SaaS architecture synthesized in 14 minutes.', status: 'Production', deployedAt: { seconds: Date.now() / 1000 - 86400 } },
          { id: 'ecomm-stack', title: 'headless_commerce', description: 'Next-gen commerce synthesis with automated inventory kinetics.', status: 'Live', deployedAt: { seconds: Date.now() / 1000 - 172800 } },
          { id: 'ai-dashboard', title: 'Neural Monitor V2', description: 'Real-time inference tracking with acoustics-ready visualizers.', status: 'Online', deployedAt: { seconds: Date.now() / 1000 - 259200 } },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeployments();
  }, []);

  const assembleHtml = (mainContent: string, allFiles: any[]) => {
    if (!mainContent || !mainContent.trim().toLowerCase().startsWith('<!doctype html>') && !mainContent.trim().toLowerCase().startsWith('<html')) {
      return mainContent || '';
    }

    let assembed = mainContent;

    if (Array.isArray(allFiles)) {
      // Inline CSS
      allFiles.forEach(file => {
        if (file.name.endsWith('.css')) {
          const linkTag = new RegExp(`<link[^>]*href=["'](?:./)?${file.name}["'][^>]*>`, 'gi');
          if (linkTag.test(assembed)) {
            assembed = assembed.replace(linkTag, `<style>${file.content}</style>`);
          } else {
            assembed = assembed.replace('</head>', `<style>${file.content}</style></head>`);
          }
        }
      });

      // Inline JS
      allFiles.forEach(file => {
        if (file.name.endsWith('.js')) {
          const scriptTag = new RegExp(`<script[^>]*src=["'](?:./)?${file.name}["'][^>]*>\\s*</script>`, 'gi');
          if (scriptTag.test(assembed)) {
            assembed = assembed.replace(scriptTag, `<script>${file.content}</script>`);
          } else {
            assembed = assembed.replace('</body>', `<script>${file.content}</script></body>`);
          }
        }
      });
    }

    return assembed;
  };

  return (
    <div className="min-h-screen bg-black text-zinc-100 font-sans selection:bg-zinc-800 selection:text-white flex flex-col">
      <Navbar />

      {/* Header */}
      <header className="px-8 py-24 border-b border-zinc-900 bg-zinc-950/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="text-zinc-500" size={24} />
              <h1 className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500">Ship Gallery</h1>
            </div>
            <h2 className="text-5xl md:text-7xl font-mono uppercase tracking-tighter mb-6">
              Published Builds
            </h2>
            <p className="text-xl text-zinc-400 font-light max-w-2xl leading-relaxed">
              Explore the latest synthesized applications and components deployed by the Yacht Labs community.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Gallery Grid */}
      <main className="flex-1 px-8 py-24">
        <div className="max-w-6xl mx-auto">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="w-8 h-8 border-2 border-zinc-800 border-t-zinc-400 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-32 border border-dashed border-red-900/50 rounded-3xl bg-red-950/20">
              <h3 className="text-xl font-mono uppercase tracking-tight text-red-400 mb-2">Error Loading Builds</h3>
              <p className="text-red-500/70 font-light max-w-md mx-auto">{error}</p>
            </div>
          ) : deployments.length === 0 ? (
            <div className="text-center py-32 border border-dashed border-zinc-800 rounded-3xl">
              <Layers className="mx-auto text-zinc-600 mb-4" size={48} />
              <h3 className="text-xl font-mono uppercase tracking-tight text-zinc-400 mb-2">No Builds Found</h3>
              <p className="text-zinc-600 font-light">There are currently no published builds to display.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {deployments.map((deployment, index) => (
                <motion.div
                  key={deployment.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group relative bg-zinc-900/30 border border-zinc-800 rounded-2xl overflow-hidden hover:border-zinc-600 transition-all flex flex-col"
                >
                  <div className="aspect-video bg-zinc-950 border-b border-zinc-800 relative overflow-hidden">
                    {deployment.coverImageUrl ? (
                      <img 
                        src={deployment.coverImageUrl} 
                        alt={deployment.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        referrerPolicy="no-referrer"
                      />
                    ) : deployment.content ? (
                      <iframe
                        srcDoc={assembleHtml(deployment.content, deployment.files)}
                        title={deployment.title}
                        className="absolute top-0 left-0 w-[400%] h-[400%] origin-top-left scale-[0.25] pointer-events-none bg-zinc-950 transition-opacity duration-700"
                        sandbox="allow-scripts"
                        tabIndex={-1}
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="absolute inset-0 opacity-20 group-hover:opacity-40 transition-opacity">
                          <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]" />
                        </div>
                        <Code2 className="text-zinc-800 group-hover:text-zinc-600 transition-colors z-10" size={48} />
                      </div>
                    )}
                    <div className="absolute inset-0 z-10" />
                  </div>
                  
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-mono uppercase tracking-tight truncate pr-4">
                        {deployment.title || 'Untitled Build'}
                      </h3>
                      <a 
                        href={`/pub/${deployment.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zinc-500 hover:text-white transition-colors shrink-0 z-20 relative"
                      >
                        <ExternalLink size={18} />
                      </a>
                    </div>
                    
                    <p className="text-sm text-zinc-500 font-light line-clamp-2 mb-6 flex-1">
                      {deployment.description || 'No description provided for this build.'}
                    </p>
                    
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-zinc-800/50">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                        {deployment.deployedAt ? new Date(deployment.deployedAt.seconds * 1000).toLocaleDateString() : 'Unknown Date'}
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-500/70 bg-emerald-500/10 px-2 py-1 rounded-full">
                        {deployment.status || 'Live'}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
